import {
  useEffect,
  useLayoutEffect,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import type { UseFormReset, UseFormSetValue } from 'react-hook-form';
import type { Sample } from '../services/sample.service';
import type { StorageRoom } from '../services/storageRoom.service';
import {
  buildSampleCreateFormData,
  buildSampleEditInit,
  type SampleEditUsageRecordRow,
} from '../utils/sampleFormInit';
import { loadLastSampleInputData } from '../utils/sampleFormLastInput';
import type { SampleFormData } from '../utils/sampleFormSchema';
import { buildUsageRecordsSignature } from '../utils/sampleUsageRecordSignature';

/** 新規ハイドレーション済みマーカー（編集の sample id と衝突しない） */
const CREATE_HYDRATED = -1;

type Args = {
  show: boolean;
  isEditMode: boolean;
  initialData?: Sample | Partial<Sample> | null;
  createLocationPrefill?: Sample | Partial<Sample> | null;
  storageRooms: StorageRoom[];
  language: string;
  /** 編集名のシード人間化用（任意） */
  t?: (key: string, options?: Record<string, unknown>) => string;
  reset: UseFormReset<SampleFormData>;
  setValue: UseFormSetValue<SampleFormData>;
  setPathogenOption: Dispatch<SetStateAction<'included' | 'excluded'>>;
  setSampleCreatedDate: Dispatch<SetStateAction<string>>;
  setUsageRecords: Dispatch<SetStateAction<SampleEditUsageRecordRow[]>>;
  setSelectedStorageRoomId: Dispatch<SetStateAction<number | undefined>>;
  selectedStorageRoomId: number | undefined;
  setStorageSearch: Dispatch<SetStateAction<string>>;
  setError: Dispatch<SetStateAction<string>>;
  formInitializedSampleIdRef: MutableRefObject<number | null>;
  initialUsageRecordsSignatureRef: MutableRefObject<string>;
};

/** モーダル表示時のフォーム初期化（編集／新規＋前回入力）。新規は1回だけ reset し、保管庫到着後は部屋だけ補正する */
export function useSampleFormModalHydration({
  show,
  isEditMode,
  initialData,
  createLocationPrefill,
  storageRooms,
  language,
  t,
  reset,
  setValue,
  setPathogenOption,
  setSampleCreatedDate,
  setUsageRecords,
  setSelectedStorageRoomId,
  selectedStorageRoomId,
  setStorageSearch,
  setError,
  formInitializedSampleIdRef,
  initialUsageRecordsSignatureRef,
}: Args): void {
  // paint 前に reset して空フォームが一瞬見えるのを防ぐ
  useLayoutEffect(() => {
    if (!show) {
      formInitializedSampleIdRef.current = null;
      return;
    }

    const editSource = isEditMode ? initialData : undefined;
    if (editSource) {
      if (formInitializedSampleIdRef.current === editSource.id) {
        return;
      }
      formInitializedSampleIdRef.current = editSource.id ?? null;

      const editInit = buildSampleEditInit(editSource, { language, t });
      setPathogenOption(editInit.pathogenOption);
      setSampleCreatedDate(editInit.sampleCreatedDate);
      setUsageRecords(editInit.usageRecords);
      initialUsageRecordsSignatureRef.current = buildUsageRecordsSignature(editInit.usageRecords);

      reset(editInit.formData);
      (Object.keys(editInit.formData) as (keyof SampleFormData)[]).forEach((key) => {
        setValue(key, editInit.formData[key]);
      });
      setSelectedStorageRoomId(editInit.storageRoomId);
      setError('');
      return;
    }

    // 新規: 1 回だけ reset（保管庫一覧の遅延到着で入力を消さない。部屋補正は Modal 側）
    if (formInitializedSampleIdRef.current === CREATE_HYDRATED) {
      return;
    }

    formInitializedSampleIdRef.current = CREATE_HYDRATED;
    const lastInputData = loadLastSampleInputData();
    const formData = buildSampleCreateFormData(lastInputData, {
      createLocationPrefill: createLocationPrefill ?? undefined,
      availableRoomIds: storageRooms.map((room) => room.id),
    });

    reset(formData);
    (Object.keys(formData) as (keyof SampleFormData)[]).forEach((key) => {
      if (key === 'storageRoomId' && formData.storageRoomId == null) return;
      setValue(key, formData[key], { shouldValidate: false });
    });
    setPathogenOption(formData.pathogenOption === 'excluded' ? 'excluded' : 'included');
    setSelectedStorageRoomId((prev) => formData.storageRoomId ?? prev);
    setStorageSearch('');
    setUsageRecords([]);
    // 作製日は毎回空（前回・シード値の持ち越しで誤った日付が入らないようにする）
    setSampleCreatedDate('');
    setError('');
  }, [
    show,
    isEditMode,
    initialData?.id,
    createLocationPrefill,
    reset,
    setValue,
    storageRooms,
    language,
    t,
    setPathogenOption,
    setSampleCreatedDate,
    setUsageRecords,
    setSelectedStorageRoomId,
    setStorageSearch,
    setError,
    formInitializedSampleIdRef,
    initialUsageRecordsSignatureRef,
    initialData,
  ]);

  // 保管庫一覧の遅延到着: 1件だけのとき新規で自動選択（入力は消さない）
  useEffect(() => {
    if (!show || isEditMode) return;
    if (selectedStorageRoomId != null) return;
    if (storageRooms.length !== 1) return;
    const onlyId = storageRooms[0].id;
    setSelectedStorageRoomId(onlyId);
    setValue('storageRoomId', onlyId, { shouldValidate: false });
  }, [
    show,
    isEditMode,
    selectedStorageRoomId,
    storageRooms,
    setSelectedStorageRoomId,
    setValue,
  ]);
}
